import type React from "react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
	RowClassParams,
} from "ag-grid-community";
import { TextField, Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { AssetPart } from "@/pages/asset-details/models/AssetPart";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

interface Props {
	allParts: AssetPart[];
	assetParts: AssetPart[];
	onSelectionChanged: (selectedParts: AssetPart[]) => void;
	isLoading: boolean;
}

export const PartsTable: React.FC<Props> = ({
	allParts,
	assetParts,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const gridApiRef = useRef<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const selectedPartsSet = useMemo(() => {
		return new Set(assetParts.map((part) => part.Name));
	}, [assetParts]);

	const sortRows = useCallback(() => {
		if (gridApiRef.current) {
			gridApiRef.current.applyColumnState({
				state: [{ colId: "Name", sort: "asc", sortIndex: 0 }],
				defaultState: { sort: null },
			});
		}
	}, []);

	const getRowStyle = useCallback((params: RowClassParams<AssetPart>) => {
		if (params.node.isSelected()) {
			return { background: "#e3f2fd" };
		}
		return undefined;
	}, []);

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allParts.map((part: AssetPart) => ({
			...part,
			isSelected: selectedPartsSet.has(part.Name),
		}));
	}, [allParts, selectedPartsSet]);

	const columns: ColDef[] = useMemo(
		() => [
			{
				field: "Name",
				headerName: "Part Name",
				minWidth: 120,
				maxWidth: 120,
				flex: 2,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "Description",
				headerName: "Description",
				flex: 4,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "BinName",
				headerName: "Bin",
				minWidth: 100,
				maxWidth: 100,
				flex: 2,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "Qty",
				headerName: "Quantity",
				minWidth: 80,
				maxWidth: 80,
				flex: 1,
				sortable: true,
				sortIndex: 1,
				type: "numericColumn",
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || 0) - (valueB || 0);
					}
				},
			},
		],
		[],
	);

	const onGridReady = useCallback((event: GridReadyEvent) => {
		gridApiRef.current = event.api;
	}, []);

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			if (assetParts && assetParts.length > 0) {
				params.api.forEachNode((node) => {
					if (selectedPartsSet.has(node.data.Name)) {
						node.setSelected(true);
					}
				});
			}
			sortRows();
			params.api.refreshClientSideRowModel("sort");
		},
		[assetParts, selectedPartsSet, sortRows],
	);

	const onAgGridSelectionChanged = useCallback(
		(event: SelectionChangedEvent) => {
			const selectedRows = event.api.getSelectedRows();
			onSelectionChanged(selectedRows);
			sortRows();
			event.api.refreshClientSideRowModel("sort");
		},
		[onSelectionChanged, sortRows],
	);

	useEffect(() => {
		if (gridApiRef.current) {
			gridApiRef.current.setGridOption("quickFilterText", filterInput.trim());
		}
	}, [filterInput]);

	useEffect(() => {
		if (gridApiRef.current && allParts.length > 0 && !isLoading) {
			setTimeout(() => {
				if (gridApiRef.current) {
					gridApiRef.current.deselectAll();
					gridApiRef.current.forEachNode((node) => {
						if (selectedPartsSet.has(node.data.Name)) {
							node.setSelected(true);
						}
					});
					sortRows();
					gridApiRef.current.refreshClientSideRowModel("sort");
				}
			}, 100);
		}
	}, [
		assetParts.length,
		allParts.length,
		isLoading,
		selectedPartsSet,
		sortRows,
	]);

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<Box
				sx={{
					width: "100%",
					height: "60vh",
					"& .ag-cell[col-id='ag-Grid-AutoColumn']": {
						paddingLeft: "8px !important",
					},
				}}
			>
				<Grid2 style={{ width: "100%", height: "60vh" }}>
					<AgGridReact<AssetPart>
						rowHeight={30}
						headerHeight={25}
						animateRows={false}
						suppressRowVirtualisation={false}
						suppressColumnVirtualisation={false}
						rowBuffer={10}
						debounceVerticalScrollbar={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={globalDefaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection={{
							mode: "multiRow",
							checkboxes: true,
							headerCheckbox: true,
							enableClickSelection: true,
							hideDisabledCheckboxes: false,
							enableSelectionWithoutKeys: true,
						}}
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
						loading={isLoading}
						getRowStyle={getRowStyle}
					/>
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
